<div class="relative w-auto">
  <!-- Trigger: default -->
  @if (variant() === 'default') {
    <button
      (click)="toggle()"
      class="flex items-center w-full justify-between gap-4 h-11 pl-3 pr-2.5 text-sm border rounded-lg bg-surface-50 text-navy-900 hover:bg-surface-100 transition-colors cursor-pointer min-w-36"
      [ngClass]="
        error() && !dateFrom() ? 'border-red-400 ring-2 ring-red-100' : 'border-surface-300'
      "
    >
      <span class="whitespace-nowrap" [ngClass]="error() && !dateFrom() ? 'text-red-400' : ''">{{
        label
      }}</span>
      <img src="/icons/calendar-icon.svg" class="w-4 h-4 shrink-0" alt="" />
    </button>
  }

  <!-- Trigger: compact -->
  @if (variant() === 'compact') {
    <button
      (click)="toggle()"
      class="flex items-center gap-1.5 bg-transparent border-0 p-0 text-xs transition-colors cursor-pointer"
      [ngClass]="
        error() && !dateFrom()
          ? 'text-red-400 hover:text-red-500'
          : 'text-slate-400 hover:text-navy-900'
      "
    >
      <img src="/icons/calendar-icon.svg" class="w-3.5 h-3.5 shrink-0" alt="" />
      <span class="whitespace-nowrap">{{ label }}</span>
      <img src="/icons/chevron-down-icon.svg" class="w-3 h-3 shrink-0" alt="" />
    </button>
  }

  <!-- Dropdown -->
  @if (open()) {
    <div
      class="fixed z-200 bg-white rounded-xl border border-surface-300 shadow-lg p-4"
      [style.top]="dropdownPos()?.top ?? null"
      [style.bottom]="dropdownPos()?.bottom ?? null"
      [style.right]="dropdownPos()?.right ?? null"
      [style.left]="dropdownPos()?.left ?? null"
    >
      <div class="flex gap-4">
        <!-- Left month -->
        <div>
          <div class="relative flex items-center justify-between mb-2">
            <button
              (click)="prev()"
              class="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-surface-200 transition-colors cursor-pointer"
            >
              <img src="/icons/chevron-left-icon.svg" class="w-4 h-4" alt="" />
            </button>

            <div class="flex items-center gap-1.5 px-1">
              <span class="text-sm font-semibold text-navy-900">{{ leftMonthLabel() }}</span>
              <button
                (click)="$event.stopPropagation(); toggleLeftYear()"
                class="text-xs font-semibold text-navy-900 bg-surface-100 hover:bg-accent-100 hover:text-accent-600 rounded-md px-1.5 py-0.5 transition-colors cursor-pointer"
              >
                {{ viewYear() }}
              </button>
            </div>

            <!-- Next visible only on mobile -->
            <button
              (click)="nextLeft()"
              class="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-surface-200 transition-colors cursor-pointer md:hidden"
            >
              <img src="/icons/chevron-right-icon.svg" class="w-4 h-4" alt="" />
            </button>
            <div class="w-7 hidden md:block"></div>

            <!-- Left year popover -->
            @if (showLeftYearDropdown()) {
              <div
                class="absolute top-full mt-1 left-1/2 -translate-x-1/2 z-10 bg-white border border-surface-300 rounded-xl shadow-lg p-2"
                (click)="$event.stopPropagation()"
              >
                <div class="flex items-center justify-between mb-1.5">
                  <button
                    (click)="leftYearPageStart.update((s) => s - 12)"
                    class="w-6 h-6 flex items-center justify-center rounded hover:bg-surface-200 cursor-pointer"
                  >
                    <img src="/icons/chevron-left-icon.svg" class="w-3 h-3" alt="" />
                  </button>
                  <span class="text-2xs font-semibold text-slate-400 px-1">
                    {{ leftYearRange()[0] }} – {{ leftYearRange()[11] }}
                  </span>
                  <button
                    (click)="leftYearPageStart.update((s) => s + 12)"
                    class="w-6 h-6 flex items-center justify-center rounded hover:bg-surface-200 cursor-pointer"
                  >
                    <img src="/icons/chevron-right-icon.svg" class="w-3 h-3" alt="" />
                  </button>
                </div>
                <div class="grid grid-cols-3 gap-1">
                  @for (year of leftYearRange(); track year) {
                    <button
                      (click)="selectLeftYear(year)"
                      class="h-7 flex items-center justify-center rounded-lg text-xs transition-colors cursor-pointer"
                      [ngClass]="
                        year === viewYear()
                          ? 'bg-primary-600 text-white font-semibold'
                          : 'text-navy-900 hover:bg-surface-200'
                      "
                    >
                      {{ year }}
                    </button>
                  }
                </div>
              </div>
            }
          </div>

          <div class="grid grid-cols-[repeat(7,2rem)] mb-1">
            @for (h of DAY_HEADERS; track $index) {
              <div
                class="h-7 flex items-center justify-center text-2xs font-semibold text-slate-400 uppercase"
              >
                {{ h }}
              </div>
            }
          </div>

          @for (week of leftWeeks(); track $index) {
            <div class="grid grid-cols-[repeat(7,2rem)] mb-1">
              @for (day of week; track $index) {
                @if (day) {
                  @let st = state(day);
                  @let disabled = isDisabled(day);
                  <div
                    class="h-8 flex items-center justify-center"
                    [ngClass]="{
                      'bg-accent-200':
                        !disabled &&
                        (st.inRange || (st.hasRange && st.isStart) || (st.hasRange && st.isEnd)),
                      'rounded-l-full':
                        !disabled && ((st.hasRange && st.isStart) || (st.inRange && $index === 0)),
                      'rounded-r-full':
                        !disabled && ((st.hasRange && st.isEnd) || (st.inRange && $index === 6)),
                    }"
                  >
                    <button
                      (click)="click(day)"
                      (mouseenter)="!disabled && hover(day)"
                      (mouseleave)="leave()"
                      class="w-7 h-7 flex items-center justify-center rounded-full text-xs transition-colors"
                      [ngClass]="{
                        'bg-primary-600 text-white font-medium': st.isStart || st.isEnd,
                        'text-navy-900 hover:bg-surface-200 cursor-pointer':
                          !st.isStart && !st.isEnd && !disabled,
                        'text-slate-300 cursor-not-allowed': disabled,
                        'ring-1 ring-inset ring-accent-400':
                          st.isToday && !st.isStart && !st.isEnd && !disabled,
                      }"
                    >
                      {{ day.getDate() }}
                    </button>
                  </div>
                } @else {
                  <div class="h-8"></div>
                }
              }
            </div>
          }
        </div>

        <!-- Divider (desktop only) -->
        <div class="w-px bg-surface-200 self-stretch hidden md:block"></div>

        <!-- Right month (desktop only) -->
        <div class="hidden md:block">
          <div class="relative flex items-center justify-between mb-2">
            <div class="w-7"></div>

            <div class="flex items-center gap-1.5 px-1">
              <span class="text-sm font-semibold text-navy-900">{{ rightMonthLabel() }}</span>
              <button
                (click)="$event.stopPropagation(); toggleRightYear()"
                class="text-xs font-semibold text-navy-900 bg-surface-100 hover:bg-accent-100 hover:text-accent-600 rounded-md px-1.5 py-0.5 transition-colors cursor-pointer"
              >
                {{ rightYearLabel() }}
              </button>
            </div>

            <button
              (click)="next()"
              class="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-surface-200 transition-colors cursor-pointer"
            >
              <img src="/icons/chevron-right-icon.svg" class="w-4 h-4" alt="" />
            </button>

            <!-- Right year popover -->
            @if (showRightYearDropdown()) {
              <div
                class="absolute top-full mt-1 left-1/2 -translate-x-1/2 z-10 bg-white border border-surface-300 rounded-xl shadow-lg p-2"
                (click)="$event.stopPropagation()"
              >
                <div class="flex items-center justify-between mb-1.5">
                  <button
                    (click)="rightYearPageStart.update((s) => s - 12)"
                    class="w-6 h-6 flex items-center justify-center rounded hover:bg-surface-200 cursor-pointer"
                  >
                    <img src="/icons/chevron-left-icon.svg" class="w-3 h-3" alt="" />
                  </button>
                  <span class="text-2xs font-semibold text-slate-400 px-1">
                    {{ rightYearRange()[0] }} – {{ rightYearRange()[11] }}
                  </span>
                  <button
                    (click)="rightYearPageStart.update((s) => s + 12)"
                    class="w-6 h-6 flex items-center justify-center rounded hover:bg-surface-200 cursor-pointer"
                  >
                    <img src="/icons/chevron-right-icon.svg" class="w-3 h-3" alt="" />
                  </button>
                </div>
                <div class="grid grid-cols-3 gap-1">
                  @for (year of rightYearRange(); track year) {
                    <button
                      (click)="selectRightYear(year)"
                      class="h-7 flex items-center justify-center rounded-lg text-xs transition-colors"
                      [ngClass]="
                        year === rightYearLabel()
                          ? 'bg-primary-600 text-white font-semibold cursor-pointer'
                          : year < viewYear()
                            ? 'text-slate-300 cursor-not-allowed'
                            : 'text-navy-900 hover:bg-surface-200 cursor-pointer'
                      "
                    >
                      {{ year }}
                    </button>
                  }
                </div>
              </div>
            }
          </div>

          <div class="grid grid-cols-[repeat(7,2rem)] mb-1">
            @for (h of DAY_HEADERS; track $index) {
              <div
                class="h-7 flex items-center justify-center text-2xs font-semibold text-slate-400 uppercase"
              >
                {{ h }}
              </div>
            }
          </div>

          @for (week of rightWeeks(); track $index) {
            <div class="grid grid-cols-[repeat(7,2rem)] mb-1">
              @for (day of week; track $index) {
                @if (day) {
                  @let st = state(day);
                  @let disabled = isDisabled(day);
                  <div
                    class="h-8 flex items-center justify-center"
                    [ngClass]="{
                      'bg-accent-200':
                        !disabled &&
                        (st.inRange || (st.hasRange && st.isStart) || (st.hasRange && st.isEnd)),
                      'rounded-l-full':
                        !disabled && ((st.hasRange && st.isStart) || (st.inRange && $index === 0)),
                      'rounded-r-full':
                        !disabled && ((st.hasRange && st.isEnd) || (st.inRange && $index === 6)),
                    }"
                  >
                    <button
                      (click)="click(day)"
                      (mouseenter)="!disabled && hover(day)"
                      (mouseleave)="leave()"
                      class="w-7 h-7 flex items-center justify-center rounded-full text-xs transition-colors"
                      [ngClass]="{
                        'bg-primary-600 text-white font-medium': st.isStart || st.isEnd,
                        'text-navy-900 hover:bg-surface-200 cursor-pointer':
                          !st.isStart && !st.isEnd && !disabled,
                        'text-slate-300 cursor-not-allowed': disabled,
                        'ring-1 ring-inset ring-accent-400':
                          st.isToday && !st.isStart && !st.isEnd && !disabled,
                      }"
                    >
                      {{ day.getDate() }}
                    </button>
                  </div>
                } @else {
                  <div class="h-8"></div>
                }
              }
            </div>
          }
        </div>
      </div>

      <div class="mt-3 pt-3 border-t border-surface-200 flex items-center justify-between">
        <button
          (click)="reset()"
          class="text-sm text-accent-500 hover:text-primary-600 font-semibold transition-colors cursor-pointer"
        >
          Reset
        </button>
        <button
          (click)="open.set(false)"
          [disabled]="!dateFrom()"
          class="px-4 py-1.5 text-sm font-semibold text-white rounded-lg transition-opacity"
          [ngClass]="
            !dateFrom()
              ? 'bg-slate-300 cursor-not-allowed opacity-60'
              : 'bg-accent-500 hover:opacity-90 cursor-pointer'
          "
        >
          Done
        </button>
      </div>
    </div>
  }
</div>
